import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { CLIMATE_WARM_C, FRONT_HOT_C, FRONT_DAMAGE_C, AC_FLOOR_C, num, clock } from '../../contexts/FarmStatusContext';
import { statusColor } from '../../config/sensorRanges';
import { RADIUS } from './ui';
import { trendOf } from './helpers';

/* The one coloured card: the container climate, on the deep brand green in both
   themes. White text, so its colours are white-alpha, not text tokens. */
const Featured = styled(Link)`
  display: flex;
  flex-direction: column;
  min-height: 256px;
  padding: 20px;
  border-radius: ${RADIUS}px;
  color: #fff;
  text-decoration: none;
  background:
    radial-gradient(120% 90% at 100% 0%, rgba(255, 255, 255, 0.10), transparent 60%),
    ${p => p.theme.colors.surfaceGreen};

  &:focus-visible { outline: 2px solid ${p => p.theme.colors.primary}; outline-offset: 2px; }

  .head { display: flex; justify-content: space-between; align-items: center; gap: 8px; font-size: 13px; font-weight: 500; }
  .pill {
    font-size: 12px; font-weight: 500; padding: 2px 8px; border-radius: 10px;
    background: rgba(255, 255, 255, 0.14);
    display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  }
  .pill i { width: 6px; height: 6px; border-radius: 50%; background: ${p => statusColor(p.theme, p.$status || 'normal')}; }
  .big { margin-top: 14px; display: flex; align-items: baseline; gap: 10px; }
  .big b { font-size: 46px; font-weight: 500; letter-spacing: -0.04em; line-height: 1; }
  .big span { font-size: 13px; opacity: 0.72; }
  .why { margin-top: 6px; font-size: 13px; opacity: 0.78; }
  .spark { margin-top: auto; padding-top: 12px; height: 56px; }
  /* change over 15 min / 30 min / 1 h / 3 h */
  .windows { margin-top: 10px; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 6px; }
  .windows div { padding: 6px 8px; border-radius: 8px; background: rgba(255, 255, 255, 0.08); min-width: 0; }
  .windows span { display: block; font-size: 11px; opacity: 0.7; }
  .windows b { font-size: 14px; font-weight: 500; white-space: nowrap; }
  .facts { margin-top: 12px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; border-top: 1px solid rgba(255, 255, 255, 0.14); padding-top: 12px; }
  .facts div { min-width: 0; }
  .facts b { display: block; font-size: 16px; font-weight: 500; }
  .facts span { display: block; margin-top: 2px; font-size: 12px; opacity: 0.7; }

  @media (max-width: 1100px) { min-height: 0; }
`;

/* ------------------------------------------------------------------ small charts */

/* ------------------------------------------------------------------ small charts */

/* A line over the last hours, no axes: shape only. `level` draws a dashed goal;
   `ref2` a second dashed series. `ink` / `faint` override the colours for use on
   the dark featured card. */
function Spark({ series, ref2, level, color, domain, ink, faint, fill }) {
  const theme = useTheme();
  const W = 300, H = 70;
  const pts = (series || []).filter(p => p.v != null);
  if (pts.length < 2) return <div className="spark" />;
  const all = [...pts, ...(ref2 || []).filter(p => p.v != null)].map(p => p.v).concat(level != null ? [level] : []);
  const lo0 = domain?.[0] ?? Math.min(...all);
  const hi0 = domain?.[1] ?? Math.max(...all);
  const padV = domain ? 0 : Math.max(0.3, (hi0 - lo0) * 0.15);
  const lo = lo0 - padV, hi = hi0 + padV;
  const t0 = domain?.[2] ?? pts[0].t;
  const t1 = domain?.[3] ?? pts[pts.length - 1].t;
  const x = t => ((t - t0) / Math.max(1, t1 - t0)) * W;
  const y = v => H - 3 - ((v - lo) / Math.max(1e-6, hi - lo)) * (H - 6);
  const path = list => list.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join('');
  const line = path(pts);
  const muted = faint || theme.colors.textMuted;
  return (
    <svg className="spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" aria-hidden="true">
      {fill && <path d={`${line}L${x(pts[pts.length - 1].t)},${H}L${x(pts[0].t)},${H}Z`} fill={fill} />}
      {level != null && <line x1="0" x2={W} y1={y(level)} y2={y(level)} stroke={muted} strokeWidth="1" strokeDasharray="3 4" vectorEffect="non-scaling-stroke" />}
      {ref2 && <path d={path(ref2.filter(p => p.v != null))} fill="none" stroke={muted} strokeWidth="1.2" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />}
      <path d={line} fill="none" stroke={ink || color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* The container against its goal: the six-hour line, change over four windows, AC setpoint, humidity, outside. */
export default function ContainerCard({ climate, climate6h, dehum, outside, now }) {
  // Container average over the last 6 h, for the featured climate card.
  const climateSeries = useMemo(() => (climate6h?.points || []).map((p) => {
    const v = [p.f, p.b].filter(x => x != null);
    return { t: p.t, v: v.length ? v.reduce((a, b) => a + b, 0) / v.length : null };
  }), [climate6h]);

  const climateTrend = trendOf(climateSeries, 3600);
  // The container's change over four windows, for the Container card's row.
  const climateWindows = [[900, '15 min'], [1800, '30 min'], [3600, '1 h'], [10800, '3 h']]
    .map(([back, label]) => ({ label, t: trendOf(climateSeries, back) }));

  // Featured climate card: the container against its goal.
  let climateWord = ['No data', 'offline'];
  if (climate.temp !== null) {
    const e = climate.error;
    if (e === null) climateWord = ['Live', 'normal'];
    else if (e >= CLIMATE_WARM_C) {
      // Same rule as the Needs you item: the colour follows the plants' lines, not the
      // size of the gap, because with the lamps on the AC cannot close it (Oct 2026).
      const f = climate.front;
      const level = f !== null && f >= FRONT_DAMAGE_C ? 'critical'
        : (f !== null && f >= FRONT_HOT_C) || (climate.setpoint !== null && climate.setpoint >= climate.goal && climate.setpoint > AC_FLOOR_C) ? 'warning'
        : 'notice';
      climateWord = [`${e.toFixed(1)}° over goal`, level];
    }
    else if (e > 1) climateWord = ['A little warm', 'notice'];
    else if (e < -1.5) climateWord = ['Cool', 'notice'];
    else climateWord = ['On target', 'normal'];
  }

  return (
    <Featured to="/rooms" $status={climateWord[1]} aria-label="Container climate, open Environment">
      <div className="head">
        Container
        <span className="pill"><i />{climateWord[0]}</span>
      </div>
      <div className="big">
        <b>{climate.temp !== null ? `${climate.temp.toFixed(1)}°` : '--'}</b>
        {climate.goal !== null && <span>goal {climate.goal}°</span>}
      </div>
      <div className="why">
        {climate.temp === null ? 'The container sensors are not reporting.' : (
          <>
            {climateTrend ? (Math.abs(climateTrend.delta) < 0.3 ? 'Steady over the last hour' : `${climateTrend.delta > 0 ? '↑ Up' : '↓ Down'} ${Math.abs(climateTrend.delta).toFixed(1)}° in the last hour`) : 'Last 6 hours'}
            {climate.stageText && `. AC ${climate.stageText.toLowerCase()}`}
          </>
        )}
      </div>
      {climateSeries.filter(p => p.v != null).length > 1
        ? <Spark series={climateSeries} level={climate.goal} ink="#ffffff" faint="rgba(255,255,255,0.45)" fill="rgba(255,255,255,0.08)" />
        : <div className="spark" />}
      <div className="windows" aria-label="Change in container temperature">
        {climateWindows.map(({ label, t }) => (
          <div key={label} title={t ? `Since ${clock(t.since, now)}` : 'Not enough history yet'}>
            <span>{label}</span>
            <b>{!t ? '--' : Math.abs(t.delta) < 0.1 ? '0.0°' : `${t.delta > 0 ? '↑' : '↓'} ${Math.abs(t.delta).toFixed(1)}°`}</b>
          </div>
        ))}
      </div>
      <div className="facts">
        <div><b>{climate.setpoint !== null ? `${climate.setpoint}°` : '--'}</b><span>AC setpoint</span></div>
        <div><b>{climate.rh !== null ? `${Math.round(climate.rh)} %` : '--'}</b>
          <span>{dehum?.agent_online && dehum?.switch_on ? 'humidity · drying' : 'humidity'}</span></div>
        <div><b>{outside ? `${num(outside.temp_c)?.toFixed(0)}°` : '--'}</b><span>outside</span></div>
      </div>
    </Featured>
  );
}
